export type TaxReviewLiableParty = "organizer" | "rauxa";
export type TaxReviewAddress = {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postal_code: string;
  country: string;
};
export type AdminRaceTaxReviewDraft = {
  raceEntry: { liableParty: TaxReviewLiableParty; taxCode: string };
  serviceFee: { liableParty: TaxReviewLiableParty; taxCode: string };
  registrationRule: {
    mode: "required" | "not_required" | "manual_review";
    requiredTypes: string[];
    requiredRegistrations: Array<{ type: string; jurisdiction: string }>;
  };
  legalSources: string[];
  liabilityRationale: string;
  validUntil: string | null;
  neverExpires?: boolean;
  serviceFeeScopeConfirmed: boolean;
};
export type TaxReviewExpectedComponent = {
  country: string;
  state: string;
  level: string;
  displayName: string;
  taxType: string;
  amount: number;
};
export type AdminRaceTaxReviewExpected = {
  entryTaxAmount: number;
  serviceFeeTaxAmount: number;
  entryComponents: TaxReviewExpectedComponent[];
  serviceFeeComponents: TaxReviewExpectedComponent[];
};
export type AdminRaceTaxReview = {
  raceId: string;
  revision: number;
  venue: TaxReviewAddress | null;
  organizerId: string;
  baseEntryAmount?: number | null;
  currency?: string | null;
  contextFingerprint?: string;
  savedSample?: { revision: number; savedAt: string; billingAddress: TaxReviewAddress; expected: AdminRaceTaxReviewExpected; currency: string | null; contextMatches: boolean; contextChanges: TaxReviewContextChange[] } | null;
  draft: AdminRaceTaxReviewDraft | null;
  active: { status: "active" | "revoked"; revision: number; validUntil: string | null; neverExpires?: boolean } | null;
  audits: Array<{ id: string; status: "matched" | "blocked"; createdAt: string; revision: number; issues: Array<{ code: string }>; evidence?: AdminRaceTaxAuditEvidence | null; expected?: AdminRaceTaxReviewExpected | null; billingAddress?: TaxReviewAddress | null; currency?: string | null; contextMatches?: boolean; contextChanges?: TaxReviewContextChange[] }>;
};
export type TaxReviewContextChange = "venue" | "price" | "currency" | "organizer_account" | "organizer" | "review";
export type AdminRaceTaxAuditEvidence = {
  calculationId: string;
  apiVersion: string;
  currency: string;
  amountTotal: number;
  lines: Array<{ reference: string; amount: number; amountTax: number; components: Array<TaxReviewExpectedComponent & { taxabilityReason: string }> }>;
};

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const text = (value: unknown): string => typeof value === "string" ? value.trim() : "";
const validRevision = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const validDate = (value: unknown): value is string => typeof value === "string" && Number.isFinite(Date.parse(value));
// A missing or malformed date must never be interpreted as a permanent review.
const validExpiration = (value: { neverExpires?: unknown; validUntil?: unknown }, allowEmpty = false): boolean =>
  (value.neverExpires === undefined || typeof value.neverExpires === "boolean") &&
  (value.neverExpires === true ? value.validUntil === null : validDate(value.validUntil) || (allowEmpty && value.validUntil === ""));
const invalidReview = (): never => { throw new Error("Unable to load this tax review. Refresh and try again."); };
const safeAmount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const parseBillingAddress = (value: unknown): TaxReviewAddress | null => {
  if (value === undefined || value === null) return null;
  const address = record(value);
  if (!["line1", "city", "postal_code", "country"].every((key) => text(address[key])) ||
    !/^[A-Za-z]{2}$/.test(text(address.country)) ||
    ["line2", "state"].some((key) => address[key] !== undefined && typeof address[key] !== "string")) return invalidReview();
  return { line1: text(address.line1), city: text(address.city), postal_code: text(address.postal_code), country: text(address.country).toUpperCase(),
    ...(address.line2 !== undefined ? { line2: text(address.line2) } : {}), ...(address.state !== undefined ? { state: text(address.state) } : {}) };
};
const parseContextChanges = (value: unknown, contextMatches: unknown): TaxReviewContextChange[] => {
  if (contextMatches !== undefined && typeof contextMatches !== "boolean") return invalidReview();
  const changes = value === undefined ? [] : value;
  if (!Array.isArray(changes) || !changes.every((change) => ["venue", "price", "currency", "organizer_account", "organizer", "review"].includes(change)) ||
    (contextMatches === true && changes.length)) return invalidReview();
  return changes as TaxReviewContextChange[];
};
const parseComponents = (value: unknown): Array<TaxReviewExpectedComponent & { taxabilityReason: string }> | null => {
  if (!Array.isArray(value)) return null;
  const components = value.map((entry) => {
    const item = record(entry);
    if (!safeAmount(item.amount)) return null;
    return { country: text(item.country), state: text(item.state), level: text(item.level), displayName: text(item.displayName), taxType: text(item.taxType), amount: item.amount, taxabilityReason: text(item.taxabilityReason) };
  });
  return components.some((item) => item === null) ? null : components as Array<TaxReviewExpectedComponent & { taxabilityReason: string }>;
};
const parseEvidence = (value: unknown): AdminRaceTaxAuditEvidence | null => {
  const evidence = record(value);
  if (!Array.isArray(evidence.lines) || !safeAmount(evidence.amountTotal) || !/^[A-Za-z]{3}$/.test(text(evidence.currency))) return null;
  const lines = evidence.lines.map((entry) => {
    const line = record(entry);
    const components = parseComponents(line.components);
    if (!safeAmount(line.amount) || !safeAmount(line.amountTax) || !components || !text(line.reference)) return null;
    return { reference: text(line.reference), amount: line.amount, amountTax: line.amountTax, components };
  });
  if (lines.some((line) => line === null)) return null;
  return { calculationId: text(evidence.calculationId), apiVersion: text(evidence.apiVersion), currency: text(evidence.currency).toUpperCase(), amountTotal: evidence.amountTotal, lines: lines as AdminRaceTaxAuditEvidence["lines"] };
};
const parseExpected = (value: unknown): AdminRaceTaxReviewExpected | null => {
  const expected = record(value);
  const entryComponents = parseComponents(expected.entryComponents);
  const serviceFeeComponents = parseComponents(expected.serviceFeeComponents);
  if (!safeAmount(expected.entryTaxAmount) || !safeAmount(expected.serviceFeeTaxAmount) || !entryComponents || !serviceFeeComponents) return null;
  return { entryTaxAmount: expected.entryTaxAmount, serviceFeeTaxAmount: expected.serviceFeeTaxAmount, entryComponents, serviceFeeComponents };
};

export const createEmptyTaxReviewDraft = (): AdminRaceTaxReviewDraft => ({
  raceEntry: { liableParty: "organizer", taxCode: "txcd_50012003" },
  serviceFee: { liableParty: "organizer", taxCode: "txcd_20030000" },
  registrationRule: { mode: "manual_review", requiredTypes: [], requiredRegistrations: [] },
  legalSources: [], liabilityRationale: "", validUntil: "", serviceFeeScopeConfirmed: false,
});

export const parseAdminRaceTaxReview = (value: unknown, raceId: string): AdminRaceTaxReview => {
  const data = record(value);
  if (data.raceId !== raceId || !validRevision(data.revision) || !Array.isArray(data.audits)) return invalidReview();
  const venue = record(data.venue);
  let draft: AdminRaceTaxReviewDraft | null = null;
  if (data.draft !== null) {
    const rawDraft = record(data.draft);
    const entry = record(rawDraft.raceEntry);
    const fee = record(rawDraft.serviceFee);
    const rule = record(rawDraft.registrationRule);
    if (
      !["organizer", "rauxa"].includes(String(entry.liableParty)) ||
      !["organizer", "rauxa"].includes(String(fee.liableParty)) ||
      !text(entry.taxCode) || !text(fee.taxCode) ||
      !["required", "not_required", "manual_review"].includes(String(rule.mode)) ||
      !Array.isArray(rule.requiredTypes) || !rule.requiredTypes.every((entry) => typeof entry === "string") ||
      !Array.isArray(rule.requiredRegistrations) ||
      !Array.isArray(rawDraft.legalSources) || !rawDraft.legalSources.every((source) => typeof source === "string") ||
      typeof rawDraft.serviceFeeScopeConfirmed !== "boolean" || !validExpiration(rawDraft, true)
    ) return invalidReview();
    const requiredRegistrations = rule.requiredRegistrations.map((value) => {
      const registration = record(value);
      if (!text(registration.type) || typeof registration.jurisdiction !== "string") return invalidReview();
      return { type: text(registration.type), jurisdiction: text(registration.jurisdiction) };
    });
    draft = {
      raceEntry: { liableParty: entry.liableParty as TaxReviewLiableParty, taxCode: text(entry.taxCode) },
      serviceFee: { liableParty: fee.liableParty as TaxReviewLiableParty, taxCode: text(fee.taxCode) },
      registrationRule: { mode: rule.mode as AdminRaceTaxReviewDraft["registrationRule"]["mode"], requiredTypes: rule.requiredTypes as string[], requiredRegistrations },
      legalSources: rawDraft.legalSources as string[], liabilityRationale: text(rawDraft.liabilityRationale),
      validUntil: rawDraft.validUntil as string | null, ...(rawDraft.neverExpires === true ? { neverExpires: true } : {}),
      serviceFeeScopeConfirmed: rawDraft.serviceFeeScopeConfirmed,
    };
  }
  let active: AdminRaceTaxReview["active"] = null;
  if (data.active !== null) {
    const rawActive = record(data.active);
    if (!["active", "revoked"].includes(String(rawActive.status)) || !validRevision(rawActive.revision) || !validExpiration(rawActive)) return invalidReview();
    active = { status: rawActive.status as "active" | "revoked", revision: rawActive.revision, validUntil: rawActive.validUntil as string | null,
      ...(rawActive.neverExpires === true ? { neverExpires: true } : {}) };
  }
  const audits = data.audits.map((value) => {
    const audit = record(value);
    if (!text(audit.id) || !validDate(audit.createdAt) || !Array.isArray(audit.issues)) return invalidReview();
    const contextChanges = parseContextChanges(audit.contextChanges, audit.contextMatches);
    if (audit.currency != null && !/^[A-Za-z]{3}$/.test(text(audit.currency))) return invalidReview();
    return {
      id: text(audit.id),
      status: audit.status === "matched" && audit.issues.length === 0 ? "matched" as const : "blocked" as const,
      createdAt: audit.createdAt,
      revision: validRevision(audit.revision) ? audit.revision : -1,
      issues: audit.issues.map((issue) => ({ code: text(record(issue).code) })),
      evidence: parseEvidence(audit.evidence),
      expected: parseExpected(audit.expected),
      billingAddress: parseBillingAddress(audit.billingAddress),
      currency: audit.currency == null ? null : text(audit.currency).toUpperCase(),
      ...(audit.contextMatches !== undefined ? { contextMatches: audit.contextMatches as boolean } : {}),
      contextChanges: contextChanges as TaxReviewContextChange[],
    };
  });
  if (new Set(audits.map(({ id }) => id)).size !== audits.length) return invalidReview();
  if ((data.baseEntryAmount != null && !safeAmount(data.baseEntryAmount)) || (data.currency != null && !/^[A-Za-z]{3}$/.test(text(data.currency)))) return invalidReview();
  if (data.contextFingerprint !== undefined && !/^[a-f0-9]{64}$/.test(text(data.contextFingerprint))) return invalidReview();
  let savedSample: AdminRaceTaxReview["savedSample"] = null;
  if (data.savedSample != null) {
    const sample = record(data.savedSample);
    const billingAddress = parseBillingAddress(sample.billingAddress);
    const expected = parseExpected(sample.expected);
    if (!validRevision(sample.revision) || sample.revision === 0 || !validDate(sample.savedAt) || !billingAddress || !expected || !validTaxReviewExpected(expected) ||
      (sample.currency !== null && !/^[A-Za-z]{3}$/.test(text(sample.currency))) || typeof sample.contextMatches !== "boolean") return invalidReview();
    savedSample = { revision: sample.revision, savedAt: sample.savedAt, billingAddress, expected, currency: sample.currency === null ? null : text(sample.currency).toUpperCase(), contextMatches: sample.contextMatches, contextChanges: parseContextChanges(sample.contextChanges, sample.contextMatches) };
  }
  return {
    raceId, revision: data.revision,
    venue: data.venue === null ? null : { line1: text(venue.line1), line2: text(venue.line2), city: text(venue.city), state: text(venue.state), postal_code: text(venue.postal_code), country: text(venue.country) },
    organizerId: text(data.organizerId), draft, active, audits,
    baseEntryAmount: data.baseEntryAmount == null ? null : data.baseEntryAmount as number,
    currency: data.currency == null ? null : text(data.currency).toUpperCase(),
    ...(data.contextFingerprint !== undefined ? { contextFingerprint: text(data.contextFingerprint) } : {}), savedSample,
  };
};

export const validateTaxReviewDraft = (draft: AdminRaceTaxReviewDraft): string | null => {
  if (draft.raceEntry.liableParty !== draft.serviceFee.liableParty) return "Race entry and service fee must use the same tax-liable party for this payment flow.";
  const validSource = (source: string) => { try { return new URL(source).protocol === "https:"; } catch { return false; } };
  if (!draft.legalSources.length || !draft.legalSources.every(validSource) || !draft.liabilityRationale.trim()) {
    return "Enter a legal source URL and a liability rationale.";
  }
  if (!validExpiration(draft) || (draft.neverExpires !== true && !(Date.parse(draft.validUntil!) > Date.now()))) {
    return "Choose a future expiration date or select Never expires.";
  }
  if (draft.neverExpires !== true && Date.parse(draft.validUntil!) > Date.now() + 366 * 24 * 60 * 60 * 1000) return "Set the review expiration within the next 366 days.";
  const rule = draft.registrationRule;
  const keys = [...rule.requiredTypes.map((type) => `${type.trim()}:`), ...rule.requiredRegistrations.map(({ type, jurisdiction }) => `${type.trim()}:${jurisdiction.trim()}`)];
  if (rule.mode === "required" && (!keys.length || keys.some((key) => key.startsWith(":")) || new Set(keys).size !== keys.length)) {
    return "Enter valid tax registration types and unique jurisdiction combinations.";
  }
  if (!/^txcd_\d+$/.test(draft.raceEntry.taxCode) || !/^txcd_\d+$/.test(draft.serviceFee.taxCode)) return "Unable to save this tax review. Check the fields and try again.";
  return null;
};

export const validTaxReviewExpected = (expected: AdminRaceTaxReviewExpected): boolean => {
  const validAmount = (amount: number) => Number.isSafeInteger(amount) && amount >= 0;
  return [
    { amount: expected.entryTaxAmount, components: expected.entryComponents },
    { amount: expected.serviceFeeTaxAmount, components: expected.serviceFeeComponents },
  ].every(({ amount, components }) => validAmount(amount) && components.length > 0 && components.every((component) =>
    validAmount(component.amount) && /^[A-Z]{2}$/.test(component.country) &&
    ["country", "state", "county", "city", "district"].includes(component.level) && Boolean(component.displayName.trim()) && Boolean(component.taxType.trim())
  ) && components.reduce((sum, component) => sum + component.amount, 0) === amount);
};

export const canUseTaxReviewAudit = (review: AdminRaceTaxReview, audit: AdminRaceTaxReview["audits"][number], now = Date.now()): boolean =>
  audit.status === "matched" && audit.revision === review.revision && audit.contextMatches !== false && !audit.contextChanges?.length &&
  now >= Date.parse(audit.createdAt) && now - Date.parse(audit.createdAt) <= 24 * 60 * 60 * 1000;
